import { Stack, styled, useTheme } from '@mui/material'
import { Typography, InfoTooltip, LoadingSkeleton } from 'ComponentLibrary'

export type SelectableFact = {
    title: string
    subtitle?: string
    value: string
    unit?: string
    selected?: boolean
    onClick?: () => void
}

type FactSelectionBoxProps = {
    title: string
    infoText?: string
    isSelected: boolean
    selectableFacts: SelectableFact[]
    isFactsPending?: boolean
}

export function FactMultiSelectionBox({ title, infoText, isSelected, selectableFacts, isFactsPending = false }: FactSelectionBoxProps) {
    const theme = useTheme()
    const black = theme.palette.customtext.black100
    const white = theme.palette.white
    const blue = theme.palette.primary1.blue100
    const grey = theme.palette.customtext.black54

    if (isFactsPending) {
        return <LoadingSkeleton height="110px" />
    }

    return (
        <StyledSelectionBox direction="column" gap={theme.spacing(1)} $highlighted={isSelected}>
            <Stack direction="row" justifyContent="space-between" alignItems="center">
                <Typography variant="headline4">{title}</Typography>
                {infoText && <InfoTooltip text={infoText} />}
            </Stack>
            <GridContainer>
                {selectableFacts.map(({ title, subtitle, value, unit, selected, onClick }) => (
                    <FactContainer key={`${title} - ${value}`} $selected={selected} direction="row" onClick={onClick} as="button">
                        <Stack direction="column" style={{ position: 'absolute', alignItems: 'flex-start' }}>
                            <Typography variant="bodyTextSmall" color={selected ? white : black}>
                                {title}
                            </Typography>
                            {subtitle && (
                                <Typography variant="bodyTextSmall" color={selected ? white : black}>
                                    {subtitle}
                                </Typography>
                            )}
                        </Stack>
                        <Stack
                            direction="row"
                            gap={theme.spacing(1)}
                            style={{ alignSelf: 'flex-end', alignItems: 'flex-end', marginLeft: 'auto' }}
                        >
                            <Typography variant="headline4" color={selected ? white : blue}>
                                {value}
                            </Typography>
                            {unit && (
                                <Typography variant="bodyTextSmall" color={selected ? white : grey}>
                                    {unit}
                                </Typography>
                            )}
                        </Stack>
                    </FactContainer>
                ))}
            </GridContainer>
        </StyledSelectionBox>
    )
}

const StyledSelectionBox = styled(Stack, {
    shouldForwardProp: (prop: string) => prop[0] !== '$',
})<{ $highlighted: boolean }>(({ theme, $highlighted }) => ({
    padding: '10px 16px 16px 16px',
    borderRadius: '12px',
    border: `1.5px solid ${$highlighted ? theme.palette.primary1.blue100 : theme.palette.customtext.black23}`,
    boxSizing: 'content-box',
}))

const GridContainer = styled('div')(({ theme }) => ({
    display: 'grid',
    gridTemplateColumns: 'repeat(2, 1fr)',
    gap: theme.spacing(2),
}))

const FactContainer = styled(Stack, {
    shouldForwardProp: (prop: string) => prop[0] !== '$',
})<{ $selected?: boolean }>(({ theme, $selected }) => ({
    borderRadius: '4px',
    padding: theme.spacing(2),
    backgroundColor: $selected ? theme.palette.primary2.green100 : theme.palette.secondary1.grey,
    '&:hover': {
        backgroundColor: $selected ? theme.palette.green.green110 : theme.palette.customtext.black20,
    },
    height: '50px',
    boxSizing: 'border-box',
    cursor: 'pointer',
    overflow: 'hidden',
    position: 'relative',
    border: 'unset',
}))
